import React, {Component} from "react";
import {connect} from "react-redux";
import {Menu} from "antd";
import {
  BarChartOutlined, BookOutlined, CreditCardOutlined, DashboardOutlined,
  ApiOutlined, FolderOpenOutlined, MessageOutlined, RiseOutlined, SendOutlined, SettingOutlined, TeamOutlined
} from "@ant-design/icons";
import { injectIntl } from 'react-intl';
import {Link} from "react-router-dom";
import CustomScrollbars from "util/CustomScrollbars";
import Auxiliary from "util/Auxiliary";
import SidebarLogo from "./SidebarLogo";
import { NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR, NAV_STYLE_NO_HEADER_MINI_SIDEBAR } from "../../constants/ThemeSetting";
import IntlMessages from "../../util/IntlMessages";
import UserProfile from "./UserProfile";
import { hideLeftSidebar, initializeWebSocketsChannel, setChatId } from "../../appRedux/actions";
import { isStripeEnabled } from "../../util/stripeService";
import { isTrialExpiredSelector, trialTimeSelector } from "../../appRedux/reducers/Accounts";
import { isAwsMarketplaceApp, isLivechatApp, isShoperApp } from "../../util/appType";
import { isShoperLimited } from "../../util/shoperLimits";
import { BULK_MESSAGING } from "../../util/permissions";
import { AccountStatus } from "../../util/account";

// Embedded in Shoper: no GROW, Connections or Billing (Analytics stays, messaging only).
const SHOPER_HIDDEN_GROUPS = ['connections', 'billing'];

class SidebarContent extends Component {
  state = {
    initializeWebSocketRequestSent: false,
    // Accordion: the group of the current page starts open (set on mount).
    openKeys: [],
    // Flyouts of the collapsed sidebar; kept apart so collapsing does not
    // forget which section was expanded.
    popupKeys: [],
  };

  // The group (from getSections) that holds the current page, if any.
  activeGroupKey = pathname => {
    const sections = this.getSections();
    const selectedKey = this.getSelectedKey(sections, pathname);
    const group = sections.find(section => section.items && section.items.length > 1 && section.items.some(item => item.key === selectedKey));
    return group ? group.key : null;
  };

  componentDidMount() {
    const group = this.activeGroupKey(this.props.pathname);
    if (group) this.setState({openKeys: [group]});
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pathname !== this.props.pathname) {
      const group = this.activeGroupKey(this.props.pathname);
      if (group && !this.state.openKeys.includes(group)) {
        this.setState({openKeys: [group]});
      }
    }
  }

  // Opening a section closes the others; closing just closes it.
  onOpenChange = keys => {
    if (this.props.collapsed) {
      this.setState({popupKeys: keys});
      return;
    }
    const opened = keys.find(key => !this.state.openKeys.includes(key));
    this.setState({openKeys: opened ? [opened] : keys});
  };

  doInitWs = (jwt) => {
    if (!this.state.initializeWebSocketRequestSent) {
      // TODO: definitely not the right place to put in, but is invoked only once, needs refactor then 😢
      // Communicate with mobile app
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'loggedIn',
          payload: jwt
        }));

        // TODO: it's not the proper place for the event listener too

        const handleMessage = message => {
          console.log(message);

          if (message.data) {
            const json = JSON.parse(message.data);

            if (json.type === 'GO_TO_CONVERSATION') {
              setChatId(json.payload.conversationId);
            }
          }
        }
        window['goToConversation'] = handleMessage;
        document.addEventListener("message", handleMessage);
        window.addEventListener("message", handleMessage);
      }

      this.setState({
        initializeWebSocketRequestSent: true
      }, () => this.props.initializeWebSocketsChannel(jwt))
    }
  };

  getNoHeaderClass = (navStyle) => {
    if (navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR || navStyle === NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR) {
      return "gx-no-header-notifications";
    }
    return "";
  };

  getNavStyleSubMenuClass = (navStyle) => {
    if (navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR) {
      return "gx-no-header-submenu-popup";
    }
    return "";
  };

  label = (id, defaultMessage) => this.props.intl.formatMessage({id, defaultMessage});

  // The menu as data: top-level sections, each a link or a group of links.
  // Every page keeps its URL; only its place in the menu changed.
  getSections = () => {
    const {accountName, isBulkMessagingEnabled, isPopupEnabled, isWithoutOverdue, isZenonAccount} = this.props;
    const l = this.label;
    const isMainApp = !isShoperApp() && !isLivechatApp();
    const canMessage = isBulkMessagingEnabled && !isZenonAccount && isWithoutOverdue;
    const hasPopups = !!(canMessage && isPopupEnabled);
    const hasStripe = isStripeEnabled(accountName) && !isAwsMarketplaceApp();
    // Only a link without a condition is always shown: a condition that is undefined (e.g. an
    // account flag missing from the account data) hides it instead of falling back to "shown".
    const link = (to, text, ...condition) => (!condition.length || condition[0]
        ? {key: to.replace(/^\//, '').split('?')[0], to, text}
        : null);
    const group = (key, icon, title, items) => ({key, icon, title, items: items.filter(Boolean)});

    return [
      group('dashboard', <DashboardOutlined/>, l('sidebar.dashboard', 'Dashboard'), [
        link('/my-dashboard', l('sidebar.dashboard', 'Dashboard'), !isLivechatApp() && isWithoutOverdue),
      ]),
      group('chats', <MessageOutlined/>, l('sidebar.chats', 'Chats'), [
        link('/chat?id=&tab=2', l('sidebar.chatsActive', 'Active'), isMainApp && isWithoutOverdue),
        link('/my-archives', l('sidebar.chatsArchived', 'Archived'), isMainApp && isWithoutOverdue),
      ]),
      // Zenon accounts keep their own two entries
      group('phone-book', <BookOutlined/>, l('sidebar.phoneBook', 'Phone book'), [
        link('/phone-book', l('sidebar.phoneBook', 'Phone book'), isZenonAccount && isWithoutOverdue),
      ]),
      group('bulk-send', <SendOutlined/>, l('sidebar.bulkSend', 'Bulk send'), [
        link('/bulk-send', l('sidebar.bulkSend', 'Bulk send'), isZenonAccount && isWithoutOverdue),
      ]),
      // Contacts, blocklist, imports, tags and custom fields are tabs on one page.
      group('contacts-group', <TeamOutlined/>, l('sidebar.contacts', 'Contacts'), [
        link('/contacts', l('sidebar.contacts', 'Contacts'), canMessage),
      ]),
      group('grow', <RiseOutlined/>, l('sidebar.grow', 'GROW'), [
        link('/forms', l('sidebar.forms', 'Sign-up forms'), hasPopups),
        link('/qr-and-nfc', l('sidebar.qrNfc', 'QR & NFC'), isWithoutOverdue && !isShoperLimited()),
        link('/keywords', l('keywords', 'Keywords'), canMessage),
        link('/coupons', l('coupons', 'Coupons'), hasPopups),
        link('/automations', l('automations.title', 'Automations'), hasPopups),
      ]),
      group('send', <SendOutlined/>, l('sidebar.send', 'SEND'), [
        link('/bulk/message', l('bulkSms.newBulk', 'New campaign'), canMessage),
        link('/reports/bulks', l('sidebar.bulkReports', 'Campaigns'), canMessage),
      ]),
      // Things reused across the account (forms, campaigns, chats): their own group, not under SEND.
      group('library', <FolderOpenOutlined/>, l('sidebar.library', 'LIBRARY'), [
        link('/media', l('sidebar.media', 'Media'), canMessage),
        link('/message-templates', l('sidebar.templates', 'Templates'), canMessage),
        link('/urls', l('urls', 'Shortened links'), canMessage),
      ]),
      // One page for chats, sign-up and NFC exports (filter by type there).
      group('reports-group', <BarChartOutlined/>, l('sidebar.analytics', 'Analytics'), [
        link('/analytics', l('sidebar.analyticsOverview', 'Overview'), canMessage),
        link('/reports', l('sidebar.reports', 'Reports'), canMessage),
      ]),
      {divider: true, key: 'divider-admin'},
      // One Settings page with its own section list; Billing pages keep their URLs.
      group('connections', <ApiOutlined/>, l('settings.groupConnections', 'Connections'), [
        link('/widget-integrations', l('integrations', 'Integrations'), hasPopups),
        link('/webhooks', l('webhooks', 'Webhooks'), canMessage),
      ]),
      group('settings-group', <SettingOutlined/>, l('settings.title', 'Settings'), [
        link('/settings', l('settings.title', 'Settings'), canMessage),
      ]),
      group('billing', <CreditCardOutlined/>, l('billing', 'Billing'), [
        isMainApp && {...link(hasStripe ? '/new-plans' : '/balance', l('billing', 'Billing')), billing: true},
      ]),
    ]
      // Embedded in Shoper: messaging only (see util/shoperLimits).
      .filter(section => !isShoperLimited() || !SHOPER_HIDDEN_GROUPS.includes(section.key))
      .filter(section => section.divider || section.items.length > 0);
  };

  // The selected item is the one whose path is the longest prefix of the current URL,
  // so /contacts/123 still highlights "Contact database".
  getSelectedKey = (sections, pathname) => {
    const path = (pathname || window.location.pathname).replace(/^\//, '');
    // Pages that live under another menu entry (editors, merged lists).
    const aliases = {
      videos: 'media', images: 'media', 'form-texts': 'message-templates',
      popups: 'forms', 'landing-pages': 'forms', 'embedded-newsletters': 'forms',
      'qr-codes': 'qr-and-nfc', 'nfc-signs': 'qr-and-nfc', 'nfc-tag-groups': 'qr-and-nfc',
      'unsubscribe-pages': 'forms', 'unsubscribe-page-settings': 'forms',
      'contact-tags': 'contacts', 'custom-fields': 'contacts', 'block-list': 'contacts', 'contact-imports': 'contacts',
      'phone-numbers': 'settings', notifications: 'settings',
      'new-plans': 'billing-link', payments: 'billing-link', balance: 'billing-link', 'new-plan': 'billing-link', plans: 'billing-link'
    };
    const first = path.startsWith('reports/contact-imports') ? 'contact-imports' : path.split('/')[0];
    let best = null;
    sections.forEach(section => (section.items || []).forEach(item => {
      if (path === item.key || path.startsWith(item.key + '/') || aliases[first] === item.key
        || (item.billing && aliases[first] === 'billing-link')) {
        if (!best || item.key.length > best.length) {
          best = item.key;
        }
      }
    }));
    return best;
  };

  renderLink = (item, icon) => (
    <Menu.Item key={item.key} icon={icon}>
      <Link onClick={this.props.hideLeftSidebar} to={item.to}>{item.text}</Link>
    </Menu.Item>
  );

  // A group with a single visible link is shown as that link, not as a one-item sub-menu.
  renderSection = (section) => {
    if (section.items.length === 1) {
      return this.renderLink({...section.items[0], text: section.title}, section.icon);
    }
    return (
      <Menu.SubMenu key={section.key} icon={section.icon} title={<>{section.title}</>}>
        {section.items.map(item => this.renderLink(item))}
      </Menu.SubMenu>
    );
  };

  render() {
    const {pathname, trialFinishDate, isTrialExpired, trialTime, collapsed, canCollapse, onToggleCollapsed} = this.props;
    const sections = this.getSections();
    const selectedKey = this.getSelectedKey(sections, pathname);

    return (<Auxiliary>

        {!isShoperApp() && (
            <SidebarLogo collapsed={collapsed}/>
        )}

        <div className="gx-sidebar-content">
          <CustomScrollbars className="gx-layout-sider-scrollbar">
            <Menu
              openKeys={collapsed ? this.state.popupKeys : this.state.openKeys}
              onOpenChange={this.onOpenChange}
              selectedKeys={selectedKey ? [selectedKey] : []}
              mode="inline"
              theme="dark"
              inlineCollapsed={collapsed}
              className="sidebar-menu"
            >
              {sections.map(section => section.divider
                ? <Menu.Divider key={section.key} className="sidebar-menu__divider"/>
                : this.renderSection(section))}

              {isShoperApp() && <UserProfile initWs={this.doInitWs} hiddenHtml={true}/>}

{/*              <Menu.Item key="bubble">
                <Link onClick={this.props.hideLeftSidebar} to="/bubble">
                  <IntlMessages id="bubble"/>
                </Link>
              </Menu.Item>*/}

            </Menu>
          </CustomScrollbars>
        </div>

        {!isShoperApp() && (
          <div className="sidebar-footer">
            {trialFinishDate && !collapsed && (
              <div className="trial-info">
                <span className="trial-text">
                  {isTrialExpired
                      ? <IntlMessages id="sidebar.trialExpired" />
                      : <IntlMessages id="sidebar.trialWillExpire" />}
                </span>
                <strong>{trialTime}</strong>
              </div>
            )}
            <div className="sidebar-footer__profile">
              <UserProfile
                initWs={this.doInitWs}
                collapsed={collapsed}
                canCollapse={canCollapse}
                onToggleCollapsed={onToggleCollapsed}
              />
            </div>
          </div>
        )}
      </Auxiliary>
    );
  }
}

SidebarContent.propTypes = {};
const mapStateToProps = (state) => {
  const {navStyle, themeType, locale, pathname} = state.settings;
  const isZenonAccount = !!state.accounts.phoneNumbersAll.find(number => number.isZenon);
  const isTrialExpired = isTrialExpiredSelector(state);
  const trialTime = trialTimeSelector(state);
  const isBulkMessagingEnabled = (process.env.REACT_APP_IS_BULK_MESSAGING_ENABLED === "true" || state.accounts.bulkMessagingEnabled) && state.global.permissions?.includes(BULK_MESSAGING);
  const isPopupEnabled = state.accounts.popupEnabled;
  const isNfcSignsEnabled = state.accounts.nfcSignsEnabled;
  const isWithoutOverdue = state.accounts.status !== AccountStatus.OVERDUE;
  return {navStyle, themeType, locale, pathname, isZenonAccount, accountName: state.accounts.accountName, trialFinishDate: state.accounts.trialFinishDate, isTrialExpired, trialTime, isBulkMessagingEnabled, isPopupEnabled, isWithoutOverdue, isNfcSignsEnabled }
};
export default injectIntl(connect(mapStateToProps, {initializeWebSocketsChannel, setChatId, hideLeftSidebar})(SidebarContent));

